
import { defineComponent, PropType } from 'vue';
import type { Component } from 'vue';
import Can from '@/components/Can.vue';
import IconGear from '@/icons/IconGear.vue';

export interface SettingActionCanProps {
  all?: string[];
  any?: string[];
  not?: boolean;
}

export interface SettingAction {
  icon?: Component;
  Icon?: Component;
  label: string;
  onClick?: () => void | Promise<unknown>;
  isDisabled?: boolean;
  tooltip?: string;
  all?: string[];
  any?: string[];
  not?: boolean;
  can?: SettingActionCanProps;
}

export default defineComponent({
  name: 'Setting',

  components: {
    Can,
    IconGear,
  },

  props: {
    actions: {
      type: Array as PropType<SettingAction[]>,
      default: () => [],
    },
  },

  data() {
    return {
      isOpen: false,
      dropdownStyle: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        zIndex: '9999',
      } as Record<string, string>,
    };
  },

  mounted() {
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onDocumentKeydown);
    window.addEventListener('resize', this.onViewportChanged);
    window.addEventListener('scroll', this.onViewportChanged, true);
  },

  beforeUnmount() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.onViewportChanged);
    window.removeEventListener('scroll', this.onViewportChanged, true);
  },

  methods: {
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
        return;
      }

      this.openDropdown();
    },

    openDropdown() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });
    },

    closeDropdown() {
      this.isOpen = false;
    },

    updateDropdownPosition() {
      const trigger = this.$refs.triggerRef as HTMLElement | undefined;
      const dropdown = this.$refs.dropdownRef as HTMLElement | undefined;

      if (!trigger) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const viewportPadding = 8;
      const gap = 6;
      const dropdownWidth = dropdown?.offsetWidth || 190;
      const dropdownHeight = dropdown?.offsetHeight || 0;
      const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;

      let left = rect.right - dropdownWidth;
      let top = rect.bottom + gap;

      left = Math.max(viewportPadding, Math.min(left, maxLeft));

      if (
        dropdownHeight &&
        top + dropdownHeight > window.innerHeight - viewportPadding
      ) {
        top = Math.max(viewportPadding, rect.top - dropdownHeight - gap);
      }

      this.dropdownStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: '9999',
      };
    },

    onViewportChanged() {
      if (this.isOpen) {
        this.updateDropdownPosition();
      }
    },

    onDocumentPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      const root = this.$refs.rootRef as HTMLElement | undefined;
      const dropdown = this.$refs.dropdownRef as HTMLElement | undefined;

      if (root?.contains(target) || dropdown?.contains(target)) {
        return;
      }

      this.closeDropdown();
    },

    onDocumentKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.closeDropdown();
      }
    },

    iconComponent(action: SettingAction) {
      return action.icon || action.Icon || null;
    },

    isActionDisabled(action: SettingAction) {
      return action.isDisabled || false;
    },

    actionTooltip(action: SettingAction) {
      return action.tooltip || '';
    },

    actionCanProps(action: SettingAction): SettingActionCanProps {
      return {
        all: action.can?.all || action.all,
        any: action.can?.any || action.any,
        not: action.can?.not ?? action.not,
      };
    },

    hasCanConfig(action: SettingAction) {
      const canProps = this.actionCanProps(action);

      return Boolean(
        canProps.not ||
          (Array.isArray(canProps.all) && canProps.all.length > 0) ||
          (Array.isArray(canProps.any) && canProps.any.length > 0)
      );
    },

    async handleActionClick(action: SettingAction) {
      if (this.isActionDisabled(action)) {
        return;
      }

      await action.onClick?.();
      this.closeDropdown();
    },
  },
});
